
import { Injectable } from '@angular/core';
import { CookiesService } from '@services/cookies.service';
import _ from 'lodash';

@Injectable({ providedIn: 'root' })
export class SankeyOthersService {
  private readonly percentageThreshold = 2; // hide if input value percent contribution is less than this
  private readonly productThreshold = 2; // hide if low contribution inputs are more than this
  private othersExpanded = new Map<number, boolean>();
  private nodes: any[];
  private links: any[];
  constructor(private cookiesService: CookiesService) {}

  get isEnabled() {
    return this.cookiesService.getCookie('sankey_group') === 'true';
  }

  get isDebug() {
    return this.isEnabled && this.hasFeatureFlag('sankey_group_debug');
  }

  get hasGroupedNodes() {
    // check whether any low contribution node is present in the sankey
    return this.nodes.some((node) => node.hidden);
  }

  // store all processes id
  get processIds(): number[] {
    return this.nodes
      .filter((p) => p.type === 'process')
      .map((p) => p.id);
  }

  toggleExpandedNode(id: number) {
    const expanded = !this.othersExpanded.get(id);
    this.othersExpanded.set(id, expanded);

    return this.nodes
      .filter((p) => p.type === 'process' && p.parents.includes(id) && p.others?.length > 0)
      .forEach((p) => {
        if (!expanded && p.id !== id) {
          this.othersExpanded.set(p.id, expanded);
        }
      });
  }

  isExpanded(id: number) {
    return this.othersExpanded.get(id) || false;
  }

  filterData(data: any) {
    this.nodes = _.cloneDeep(data.nodes);
    this.links = _.cloneDeep(data.links);

    this.calculateOtherOnLinks();
    this.setOtherOnNodes();
    this.hideOtherLinksAndNodes();

    if (this.isDebug) {
      console.table(this.links.map((n) => _.pick(n, ['id', 'source', 'target', 'percOnTarget'])));
      console.table(this.nodes.map((n) => _.pick(n, ['id', 'parents', 'others'])));
    }

    data.nodes = this.nodes;
    data.links = this.links;
    return data;
  }

  private calculateOtherOnLinks() {
    const productCount = new Map<number, number>();

    this.links
      .filter((link) => this.processIds.includes(link.target))
      .forEach((link) => {
        // find the output process
        const output = this.links.find((l) => l.source === link.target);
        // get output value if units are same for input and output.
        if (output && output?.inunit === link.target?.inunit) {
          const targetValue = output.value;
          link.percOnTarget = (link.value / targetValue) * 100;
          // compare input value percent to output value
          let count = productCount.get(link.target) || 0;
          if (link.percOnTarget <= this.percentageThreshold) {
            // count hidden nodes
            productCount.set(link.target, ++count);

            if (count > this.productThreshold) {
              this.setRecursiveOthers(link, link.target);
            }
          }
        }
      });
  }

  private setRecursiveOthers(link: any, mainTarget: number) {
    link.other = true;
    link.mainTarget = mainTarget;

    this.links
      .filter((l) => l.target === link.source) // todo: add check on unit
      .forEach((l) => this.setRecursiveOthers(l, mainTarget));
  }

  private setOtherOnNodes() {
    this.links.filter((link) => link.other)
      .reduce((prev, curr) => (prev.includes(curr.mainTarget) ? prev : [...prev, curr.mainTarget]), [])
      .map((id) => this.nodes.find((n) => n.id === id))
      .forEach((node) => {
        node.others = this.links.filter((l) => l.mainTarget === node.id);
      });
  }

  private hideOtherLinksAndNodes() {
    const nodeIndexes = [];

    this.nodes
      .filter((node) => node.others?.length > 0)
      .forEach((node) => {
        if (!this.othersExpanded.get(node.id)) {
          const otherNodes = node.others.map((link) => this.nodes.findIndex((n) => n.id === link.source));
          nodeIndexes.push(...otherNodes);
        }
      });

    this.nodes
      .filter((node) => nodeIndexes.includes(node.id))
      .forEach((node) => {
        node.hidden = true;
      });
  }

  private hasFeatureFlag(name: string) {
    return document.cookie.includes(`${name}=true`);
  }
}
